// frontend/src/components/DistrictModule.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // <-- Use your custom api instance
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
} from '@mui/icons-material';


const DistrictModule = () => {
  const [districts, setDistricts] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [error, setError] = useState('');

  // Get user role from localStorage
  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role);
  }, []);

  // Form state holds district name only
  const [formData, setFormData] = useState({
    name: ''
  });

  // Fetch all districts on component mount
  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    try {
      const response = await api.get('/districts');
      // Ensure response.data is an array
      if (Array.isArray(response.data)) {
        setDistricts(response.data);
      } else {
        console.warn('Districts API returned non-array data:', response.data);
        setDistricts([]);
      }
      setError('');
    } catch (error) {
      console.error('Error fetching districts:', error);
      // Ensure districts remains an empty array on error
      setDistricts([]);
      if (error.response?.status === 403) {
        setError('Access denied: You do not have permission to view districts.');
      } else {
        setError('Failed to fetch districts. Please try again.');
      }
    }
  };

  // Handle changes in form fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Open the form dialog in add or edit mode
  const handleOpenForm = (district = null) => {
    if (district) {
      setEditing(true);
      setSelectedDistrict(district);
      setFormData({
        name: district.name
      });
    } else {
      setEditing(false);
      setSelectedDistrict(null);
      setFormData({
        name: ''
      });
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setFormData({ name: '' });
    setSelectedDistrict(null);
    setEditing(false);
  };

  // Submit the form data to create or update a district
  const handleSubmit = async () => {
    // Validate district name
    if (!formData.name || formData.name.trim() === '') {
      setError('District name is required.');
      return;
    }

    try {
      // Only send the name field to the backend
      const payload = { name: formData.name.trim() };
      
      console.log('Submitting district:', payload);
      
      let response;
      if (editing && selectedDistrict) {
        // Update the district
        console.log('Updating district:', selectedDistrict._id);
        response = await api.put(`/districts/${selectedDistrict._id}`, payload);
      } else {
        // Create a new district
        console.log('Creating new district');
        response = await api.post('/districts', payload);
      }
      
      console.log('District saved successfully:', response.data);
      fetchDistricts();
      handleCloseForm();
      setError('');
    } catch (error) {
      console.error('Error saving district:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      if (error.response?.status === 403) {
        setError('Access denied: You do not have permission to perform this action.');
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.message) {
        setError(`Error: ${error.message}`);
      } else {
        setError('Failed to save district. Please try again.');
      }
    }
  };

  // Delete a district
  const handleDeleteDistrict = async (districtId) => {
    if (window.confirm('Are you sure you want to delete this district?')) {
      try {
        await api.delete(`/districts/${districtId}`);
        fetchDistricts();
        setError('');
      } catch (error) {
        console.error('Error deleting district:', error);
        if (error.response?.status === 403) {
          setError('Access denied: You do not have permission to delete districts.');
        } else {
          setError('Failed to delete district. Please try again.');
        }
      }
    }
  };

  const isAdmin = userRole === 'admin';

  return (
    <Container sx={{ mt: 2 }}>
      <Typography variant="h5" align="center" gutterBottom>
        {isAdmin ? 'District Management' : 'View Districts'}
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Add Button - Admin Only */}
      {isAdmin && (
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenForm()}>
          Add District
        </Button>
      )}

      <Paper sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>District Name</TableCell>
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(districts) && districts.map((district) => (
              <TableRow key={district._id}>
                <TableCell>{district.name}</TableCell>
                {isAdmin && (
                  <TableCell align="right">
                    <IconButton onClick={() => handleOpenForm(district)}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteDistrict(district._id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Dialog for adding/editing a district - Admin Only */}
      {isAdmin && (
        <Dialog open={openForm} onClose={handleCloseForm} fullWidth maxWidth="sm">
          <DialogTitle>{editing ? 'Edit District' : 'Add District'}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="District Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editing ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default DistrictModule;

